import { formatTitleCase } from "@sd/lib/ticketStreamUtils";

/** Canonical ticket categories — same values as create/edit ticket form. */
export const TICKET_CATEGORY_FILTER_VALUES = [
  "hardware",
  "software",
  "network",
  "access",
  "email",
  "security",
  "development",
  "analysis",
  "qa",
  "other",
] as const;

export const TICKET_CATEGORY_FILTER_OPTIONS = [
  { value: "hardware", label: "Hardware Issue" },
  { value: "software", label: "Software Issue" },
  { value: "network", label: "Network/Connectivity" },
  { value: "access", label: "Access Request" },
  { value: "email", label: "Email/Communication" },
  { value: "security", label: "Security Concern" },
  { value: "development", label: "Development" },
  { value: "analysis", label: "Analysis" },
  { value: "qa", label: "QA" },
  { value: "other", label: "Other" },
] as const;

const CATEGORY_LABEL_BY_VALUE = new Map(
  TICKET_CATEGORY_FILTER_OPTIONS.map(opt => [opt.value, opt.label])
);

/** Map API / grid category strings to canonical filter values. */
export function normalizeCategoryFilterValue(raw: string): string {
  const t = raw.trim().toLowerCase();
  if (!t) return "";
  if (CATEGORY_LABEL_BY_VALUE.has(t)) return t;
  const byLabel = TICKET_CATEGORY_FILTER_OPTIONS.find(
    opt =>
      opt.label.toLowerCase() === t ||
      opt.label.toLowerCase().includes(t) ||
      t.includes(opt.value)
  );
  return byLabel?.value ?? t;
}

export function normalizeCategoryFilterValues(values: string[]): string[] {
  const next = new Set<string>();
  for (const raw of values) {
    const normalized = normalizeCategoryFilterValue(raw);
    if (normalized) next.add(normalized);
  }
  return [...next];
}

/** Stable category dropdown options (canonical list + any extra values from tickets). */
export function buildCategoryFilterSelectOptions(extraValues: string[] = []) {
  const byValue = new Map(
    TICKET_CATEGORY_FILTER_OPTIONS.map(opt => [opt.value, { ...opt }])
  );

  for (const raw of extraValues) {
    const value = normalizeCategoryFilterValue(raw);
    if (!value || byValue.has(value)) continue;
    byValue.set(value, {
      value,
      label: CATEGORY_LABEL_BY_VALUE.get(value) ?? formatTitleCase(value),
    });
  }

  return [...byValue.values()];
}
